import { ExplorerUIState } from "#next/redux/explorerSlice";
import { useAppSelector } from "#next/redux/reduxHooks";
import { SearchResult } from "#next/utils/interfaces";
import { createContext, useContext } from "react";

/**
 * Context that provides the search results key for the current search results.
 * This allows child components to use `useSearchResults` and `useExplorerSelector`
 * without having to know which set of search results they're operating on.
 */
export const ExplorerSearchResultsContext = createContext<
  | {
      searchResultsKey: string;
    }
  | Record<string, never>
>({});

export const useSearchResultsKey = () =>
  useContext(ExplorerSearchResultsContext).searchResultsKey;

// Return constant array rather than new array to maintain referential equality
const NO_RESULTS_ARRAY: SearchResult[] = [];

// Helper hook that gets the current set of search results for rendering
export function useSearchResults(): SearchResult[] {
  const { searchResultsKey } = useContext(ExplorerSearchResultsContext);
  const results = useAppSelector((state) => {
    const uiState = state.explorerReducer[searchResultsKey];
    if (!uiState) {
      return null;
    }
    return uiState.filteredSearchResults;
  });
  if (results === null) {
    return NO_RESULTS_ARRAY;
  }
  return results;
}

// Helper hook that gets the current set of search results for the feed
export function useFeedSearchResults(): SearchResult[] {
  const { searchResultsKey } = useContext(ExplorerSearchResultsContext);
  const results = useAppSelector((state) => {
    const uiState = state.explorerReducer[searchResultsKey];
    if (!uiState) {
      return null;
    }
    return uiState.feedSearchResults;
  });
  if (results === null) {
    return NO_RESULTS_ARRAY;
  }
  return results;
}

// Helper hook that gets the current set of search results for the feed
export function useOriginalSearchResults(): SearchResult[] {
  const { searchResultsKey } = useContext(ExplorerSearchResultsContext);
  const results = useAppSelector((state) => {
    const uiState = state.explorerReducer[searchResultsKey];
    if (!uiState) {
      return null;
    }
    return uiState.originalSearchResults;
  });
  if (results === null) {
    return NO_RESULTS_ARRAY;
  }
  return results;
}

// Helper hook so that code using selectors doesn't need to be aware of which
// search it's operating on.
export function useExplorerSelector<F>(
  selector: (state: ExplorerUIState) => F,
) {
  const { searchResultsKey } = useContext(ExplorerSearchResultsContext);
  return useAppSelector((state) => {
    const uiState = state.explorerReducer[searchResultsKey];
    if (!uiState) {
      throw new Error(
        `Didn't find expected UI state for searchResultsKey ${searchResultsKey}`,
      );
    }
    return selector(uiState);
  });
}
